import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import MakePaymentForm from './ACHPaymentForm';
import CircularLoader from 'src/components/CircularLoader';
import paymentService from 'src/services/paymentService';
import { createAxiosInstance } from 'src/utils/axios';

const paymentServiceInstance = new paymentService(
    createAxiosInstance(process.env.REACT_APP_CASH_QORE_API)
);

/**
 * ACH Payment component that handles fetching payment methods and submitting payments.
 * Manages loading states and error handling for the payment form.
 */
const ACHPayment = ({
    clientId,
    paymentAmount,
    notes = '',
    classId,
    subsidiaryId,
    accountId,
    arAccountId,
    bankCode,
    processorCode,
    countryCheck,
    accountType,
    fileFormatId,
    lineItems = [],
    clientName,
    clientTypeId,
    onSuccess,
    onCancel,
    onFailure,
    allowPaymentDateSelection = false
}) => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false); 
    const [error, setError] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    // Fetch payment methods from API
    useEffect(() => {
        const fetchPaymentMethods = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const data = await paymentServiceInstance.getPaymentMethods(clientId);
                setPaymentMethods(data);

            } catch (err) {
                console.error('Error fetching payment methods:', err);
                setError(err.message || 'Failed to load payment methods');
                enqueueSnackbar('Error loading payment methods', { variant: 'error' });
            } finally {
                setIsLoading(false);
            }
        };

        if (clientId) {
            fetchPaymentMethods();
        } else {
            setIsLoading(false);
        }
    }, [clientId]);

    const handlePaymentSubmit = async (paymentData) => {
        if (isSubmitting) return; // Prevent multiple submissions
        
        try {
            setIsSubmitting(true);
            
            // Add all required attributes to the payment data
            const completePaymentData = {
                ...paymentData,
                paymentAmount,
                notes,
                clientId,
                classId,
                subsidiaryId,
                accountId,
                arAccountId,
                bankCode,
                processorCode,
                countryCheck,
                accountType,
                fileFormatId,
                lineItems,
                clientName,
                clientTypeId
            };

            // Submit payment through the service
            const result = await paymentServiceInstance.submitPayment(clientId, completePaymentData);

            // Handle success
            enqueueSnackbar('Payment submitted successfully!', { variant: 'success' });
            
            // Optional: Call onSuccess callback if provided
            if (typeof onSuccess === 'function') {
                onSuccess(result);
            }
        } catch (err) {
            if (typeof onFailure === 'function') {
                onFailure(err);
            }
            enqueueSnackbar(
                err.message || 'Error submitting payment. Please try again.', 
                { variant: 'error' }
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePaymentCancel = () => {
        console.log('Payment cancelled');
        
        // Optional: Call onCancel callback if provided
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    // Show loading state
    if (isLoading) {
        return <CircularLoader />;
    }

    // Show error state
    if (error) {
        return (
            <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                p={3}
            >
                <Typography variant="h6" color="error" gutterBottom>
                    Error loading payment methods
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <MakePaymentForm
                isSubmitting={isSubmitting}
                paymentMethods={paymentMethods}
                onSubmit={handlePaymentSubmit}
                onCancel={handlePaymentCancel}
                allowPaymentDateSelection={allowPaymentDateSelection}
            />
        </Box>
    );
};

ACHPayment.propTypes = {
    /**
     * Whether to allow user to select payment date
     */
    allowPaymentDateSelection: PropTypes.bool,
    /**
     * Unique identifier for the client making the payment
     */
    clientId: PropTypes.string.isRequired,
    /**
     * The amount to be paid - can be string or number
     */
    paymentAmount: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    /**
     * Additional notes for the payment
     */
    notes: PropTypes.string,
    /**
     * Class identifier for the payment
     */
    classId: PropTypes.string,
    /**
     * Subsidiary identifier for the payment
     */
    subsidiaryId: PropTypes.string,
    /**
     * Account identifier for the payment
     */
    accountId: PropTypes.string,
    /**
     * Accounts Receivable identifier for the payment
     */
    arAccountId: PropTypes.string,
    /**
     * Bank code for the payment
     */
    bankCode: PropTypes.string,
    /**
     * Processor code for the payment
     */
    processorCode: PropTypes.string,
    /**
     * Country check for the payment
     */
    countryCheck: PropTypes.string,
    /**
     * Account type for the payment
     */
    accountType: PropTypes.string,
    /**
     * File format identifier for the payment
     */
    fileFormatId: PropTypes.string,    
    /**
     * Array of line items associated with the payment
     */
    lineItems: PropTypes.arrayOf(PropTypes.object),
    /**
     * Name of the client making the payment
     */
    clientName: PropTypes.string,
    /**
     * Type identifier for the client
     */
    clientTypeId: PropTypes.string,
    /**
     * Callback function called when payment is successfully submitted
     */
    onSuccess: PropTypes.func,
    /**
     * Callback function called when payment is cancelled
     */
    onCancel: PropTypes.func,
    /**
     * Callback function called when payment fails
     */
    onFailure: PropTypes.func,
};

ACHPayment.defaultProps = {
    notes: '',
    lineItems: []
};

export default ACHPayment;